import { LevelScore, UserProfile, UserProgress } from '../../types/progress';
import { IDatabaseService } from './IDatabaseService';

/**
 * LocalStorageService - Fallback database for offline/testing
 * Stores all data in browser localStorage
 */
export class LocalStorageService implements IDatabaseService {
  private readonly USERS_KEY = 'code_trainer_users';
  private readonly PROGRESS_KEY = 'code_trainer_progress';
  private readonly HISTORY_KEY = 'code_trainer_history';

  // User Profile Operations
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    const users = this.getFromStorage<Record<string, UserProfile>>(this.USERS_KEY, {});
    const profile = users[userId];
    
    if (profile) {
      return {
        ...profile,
        createdAt: new Date(profile.createdAt),
        lastLoginAt: new Date(profile.lastLoginAt),
      };
    }
    return null;
  }

  async createUserProfile(profile: UserProfile): Promise<void> {
    const users = this.getFromStorage<Record<string, UserProfile>>(this.USERS_KEY, {});
    users[profile.uid] = profile;
    this.saveToStorage(this.USERS_KEY, users);
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    const users = this.getFromStorage<Record<string, UserProfile>>(this.USERS_KEY, {});
    if (users[userId]) {
      users[userId] = { ...users[userId], ...updates };
      this.saveToStorage(this.USERS_KEY, users);
    }
  }

  // User Progress Operations
  async getUserProgress(
    userId: string,
    language: string,
    keyboardId: string
  ): Promise<UserProgress | null> {
    const progress = this.getFromStorage<Record<string, UserProgress>>(this.PROGRESS_KEY, {});
    const key = `${userId}_${language}_${keyboardId}`;
    const userProgress = progress[key];
    
    if (userProgress) {
      return {
        ...userProgress,
        levelScores: userProgress.levelScores.map(score => ({
          ...score,
          completedAt: new Date(score.completedAt),
        })),
      };
    }
    return null;
  }

  async createUserProgress(progress: UserProgress): Promise<void> {
    const allProgress = this.getFromStorage<Record<string, UserProgress>>(this.PROGRESS_KEY, {});
    const key = `${progress.userId}_${progress.language}_${progress.keyboardId}`;
    allProgress[key] = progress;
    this.saveToStorage(this.PROGRESS_KEY, allProgress);
  }

  async updateUserProgress(
    userId: string,
    language: string,
    keyboardId: string,
    updates: Partial<UserProgress>
  ): Promise<void> {
    const allProgress = this.getFromStorage<Record<string, UserProgress>>(this.PROGRESS_KEY, {});
    const key = `${userId}_${language}_${keyboardId}`;
    
    if (allProgress[key]) {
      allProgress[key] = { ...allProgress[key], ...updates };
      this.saveToStorage(this.PROGRESS_KEY, allProgress);
    }
  }

  async getAllUserProgress(userId: string): Promise<UserProgress[]> {
    const allProgress = this.getFromStorage<Record<string, UserProgress>>(this.PROGRESS_KEY, {});
    return Object.values(allProgress)
      .filter(p => p.userId === userId)
      .map(p => ({
        ...p,
        levelScores: p.levelScores.map(score => ({
          ...score,
          completedAt: new Date(score.completedAt),
        })),
      }));
  }

  // Level History Operations
  async saveLevelScore(userId: string, levelScore: LevelScore): Promise<void> {
    const allHistory = this.getFromStorage<Record<string, LevelScore[]>>(this.HISTORY_KEY, {});
    
    if (!allHistory[userId]) {
      allHistory[userId] = [];
    }
    
    allHistory[userId].push(levelScore);
    this.saveToStorage(this.HISTORY_KEY, allHistory);
  }

  async getLevelHistory(userId: string, limit: number = 50): Promise<LevelScore[]> {
    const allHistory = this.getFromStorage<Record<string, LevelScore[]>>(this.HISTORY_KEY, {});
    const history = allHistory[userId] || [];
    
    return history
      .map(score => ({
        ...score,
        completedAt: new Date(score.completedAt),
      }))
      .sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime())
      .slice(0, limit);
  }

  // Utility
  async deleteUserData(userId: string): Promise<void> {
    // Delete user profile
    const users = this.getFromStorage<Record<string, UserProfile>>(this.USERS_KEY, {});
    delete users[userId];
    this.saveToStorage(this.USERS_KEY, users);
    
    // Delete user progress
    const allProgress = this.getFromStorage<Record<string, UserProgress>>(this.PROGRESS_KEY, {});
    Object.keys(allProgress).forEach(key => {
      if (key.startsWith(`${userId}_`)) {
        delete allProgress[key];
      }
    });
    this.saveToStorage(this.PROGRESS_KEY, allProgress);
    
    // Delete level history
    const allHistory = this.getFromStorage<Record<string, LevelScore[]>>(this.HISTORY_KEY, {});
    delete allHistory[userId];
    this.saveToStorage(this.HISTORY_KEY, allHistory);
  }

  // Private helpers
  private getFromStorage<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return defaultValue;
    }
  }

  private saveToStorage(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error writing to localStorage (${key}):`, error);
    }
  }
}

export const localStorageService = new LocalStorageService();

